/**
 * Shared status-notice constants: the canonical list of *site areas* a notice
 * can target, plus severity definitions. This module is the single source of
 * truth for which parts of the site exist as banner targets — used by both the
 * public banner (route matching) and the admin panel (area checkboxes).
 *
 * Extending coverage to a new page/section is a one-place change: add an entry
 * to {@link SITE_AREAS} with a `match(pathname)` predicate and an i18n
 * `labelKey`. No backend migration is needed — the API stores area keys as
 * free-form strings.
 */

/** Sentinel area key meaning "show on every page" (mirrors the API). */
export const ALL_AREAS = 'all';

/**
 * Ordered site-area definitions. `match(pathname)` decides whether a notice
 * targeting this area is shown on the given route. `all` always matches.
 */
export const SITE_AREAS = [
  { key: 'all', labelKey: 'notices.areaAll', match: () => true },
  { key: 'home', labelKey: 'notices.areaHome', match: (p) => p === '/' },
  {
    key: 'play',
    labelKey: 'notices.areaPlay',
    match: (p) => /^\/(create|queue|open-slots|lobby|veto)(\/|$)/.test(p),
  },
  { key: 'matches', labelKey: 'notices.areaMatches', match: (p) => p.startsWith('/stats/matches') },
  {
    key: 'leaderboard',
    labelKey: 'notices.areaLeaderboard',
    match: (p) =>
      p.startsWith('/stats/leaderboard')
      || p.startsWith('/stats/season')
      || p.startsWith('/stats/players')
      || p.startsWith('/stats/maps')
      || p.startsWith('/stats/medaljer'),
  },
  { key: 'tournaments', labelKey: 'notices.areaTournaments', match: (p) => p.startsWith('/tournaments') },
  { key: 'leagues', labelKey: 'notices.areaLeagues', match: (p) => p.startsWith('/leagues') },
  { key: 'news', labelKey: 'notices.areaNews', match: (p) => p.startsWith('/news') },
  { key: 'live', labelKey: 'notices.areaLive', match: (p) => p.startsWith('/live') },
];

/** Lookup map: area key -> definition. */
export const SITE_AREA_MAP = SITE_AREAS.reduce((acc, a) => {
  acc[a.key] = a;
  return acc;
}, {});

/**
 * Decide whether a notice targeting ``areas`` should show on ``pathname``.
 * An empty list or one containing {@link ALL_AREAS} matches every route;
 * unknown keys are ignored.
 *
 * @param {string[]} areas - Area keys the notice targets.
 * @param {string} pathname - Current router pathname.
 * @returns {boolean} True when the notice should be shown on this route.
 */
export function noticeMatchesPath(areas, pathname) {
  if (!Array.isArray(areas) || areas.length === 0 || areas.includes(ALL_AREAS)) return true;
  return areas.some((key) => {
    const area = SITE_AREA_MAP[key];
    return area ? area.match(pathname) : false;
  });
}

/** Severity definitions (value mirrors the API; emoji/labelKey for the UI). */
export const NOTICE_SEVERITIES = [
  { value: 'info', labelKey: 'notices.sevInfo', emoji: 'ℹ️' },
  { value: 'warning', labelKey: 'notices.sevWarning', emoji: '⚠️' },
  { value: 'critical', labelKey: 'notices.sevCritical', emoji: '🚨' },
];

/** Lookup map: severity value -> definition. */
export const NOTICE_SEVERITY_MAP = NOTICE_SEVERITIES.reduce((acc, s) => {
  acc[s.value] = s;
  return acc;
}, {});
